import { GameAudio } from "./Audio";
import { calculateThrowVelocity, clamp, type MotionSample } from "./physics";
import {
  BOAT_SPECS,
  BOAT_SINK_SECONDS,
  BOAT_SPAWN_X,
  DUNE_X,
  FIXED_STEP,
  GRAB_RADIUS,
  GRAVITY,
  HEIGHT,
  MAX_THROW_SPEED,
  SAFE_DISTANCE,
  SHORE_X,
  WATER_Y,
  WIDTH,
  SeededRandom,
  addStrikes,
  boatSpeed,
  canGrab,
  canSpawnBoat,
  chooseBoatType,
  comboMultiplier,
  isActiveCrewPhase,
  isGameOver,
  landsOnBoatDeck,
  parseRefugeeSave,
  passengerCount,
  pointsForSplash,
  randomSeed,
  spawnIntervalSeconds,
  type BoatSpec,
  type RefugeePhase,
  type RefugeeSave,
} from "./rules";
import { readStoredNumber, readStoredValue, writeStoredValue } from "./storage";

type GameState = "menu" | "countdown" | "playing" | "paused" | "results";
type HairStyle = "fade" | "curls" | "braids" | "high-top" | "buzz";

interface Refugee {
  id: number;
  boatId: number | null;
  originBoatId: number | null;
  seatOffset: number;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  velocityX: number;
  velocityY: number;
  phase: RefugeePhase;
  touched: boolean;
  rotation: number;
  angularVelocity: number;
  bounces: number;
  runDelay: number;
  runSpeed: number;
  swimTimer: number;
  sinkTimer: number;
  fade: number;
  anim: number;
  skin: string;
  skinShadow: string;
  shirt: string;
  shirtShadow: string;
  vest: boolean;
  hair: HairStyle;
  blinkTimer: number;
}

interface Boat {
  id: number;
  spec: BoatSpec;
  x: number;
  speed: number;
  wobble: number;
  sinking: boolean;
  sinkTimer: number;
}

interface Particle {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  life: number;
  maximumLife: number;
  color: string;
  radius: number;
  gravity: number;
}

interface FloatingText {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

export interface GameElements {
  shell: HTMLElement;
  canvas: HTMLCanvasElement;
  overlay: HTMLElement;
  score: HTMLElement;
  combo: HTMLElement;
  lives: HTMLElement;
  best: HTMLElement;
  clock: HTMLElement;
  pauseButton: HTMLButtonElement;
  soundButton: HTMLButtonElement;
  fullscreenButton: HTMLButtonElement;
}

const SKINS = [
  ["#6f3d20", "#472412"],
  ["#81502d", "#563019"],
  ["#955b32", "#64371d"],
  ["#a76a3b", "#704322"],
  ["#bd8050", "#81532f"],
] as const;
const SHIRTS = [
  ["#b0413e", "#6e2422"],
  ["#3e6b8a", "#24455c"],
  ["#c9a227", "#876c15"],
  ["#5d8a4a", "#3a5c2e"],
  ["#7a5d8a", "#4b3757"],
  ["#8a8f96", "#565b62"],
] as const;
const HAIR: readonly HairStyle[] = ["fade", "curls", "braids", "high-top", "buzz"];
const SAVE_KEY = "rapid-refugees-save";
const LAND_Y = WATER_Y - 12;

export class RapidRefugeesGame {
  private readonly elements: GameElements;
  private readonly context: CanvasRenderingContext2D;
  private readonly audio: GameAudio;
  private readonly boats: Boat[] = [];
  private readonly refugees: Refugee[] = [];
  private readonly particles: Particle[] = [];
  private readonly floatingTexts: FloatingText[] = [];
  private readonly pointerSamples: MotionSample[] = [];
  private state: GameState = "menu";
  private previousState: GameState = "menu";
  private grabbed: Refugee | null = null;
  private activePointerId: number | null = null;
  private pointerX = 0;
  private pointerY = 0;
  private score = 0;
  private comboStreak = 0;
  private strikes = 0;
  private best = 0;
  private bestBeforeRun = 0;
  private deported = 0;
  private slippedThrough = 0;
  private longestYeet = 0;
  private elapsed = 0;
  private spawnTimer = 2.2;
  private countdownRemaining = 0;
  private countdownBeat = 4;
  private lastFrame = performance.now();
  private accumulator = 0;
  private animationFrame = 0;
  private shake = 0;
  private flash = 0;
  private soundEnabled: boolean;
  private save: RefugeeSave;
  private menuExpanded = false;
  private frozen = false;
  private refugeeId = 0;
  private boatId = 0;
  private gameplayRandom = new SeededRandom(randomSeed());
  private visualRandom = new SeededRandom(randomSeed());

  constructor(elements: GameElements) {
    this.elements = elements;
    const context = elements.canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D rendering is unavailable");
    this.context = context;
    this.elements.canvas.width = WIDTH;
    this.elements.canvas.height = HEIGHT;
    this.soundEnabled = readStoredValue("rapid-refugees-sound") !== "off";
    this.audio = new GameAudio(this.soundEnabled);
    this.save = parseRefugeeSave(readStoredValue(SAVE_KEY), readStoredNumber("rapid-refugees-best"));
    this.best = this.save.best;
    this.bindEvents();
    this.updateHud();
    this.renderOverlay();
    this.updateControls();

    const shot = new URLSearchParams(location.search).get("shot");
    if (shot) this.prepareShot(shot);
    this.animationFrame = requestAnimationFrame(this.loop);
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    this.elements.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.elements.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.elements.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.elements.canvas.removeEventListener("pointercancel", this.handlePointerCancel);
    this.elements.overlay.removeEventListener("click", this.handleOverlayClick);
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  private readonly loop = (now: number): void => {
    if (this.frozen) {
      this.draw(now / 1_000);
      return;
    }

    const elapsed = Math.min(Math.max(0, (now - this.lastFrame) / 1_000), 0.05);
    this.lastFrame = now;
    this.accumulator = Math.min(this.accumulator + elapsed, 0.2);
    while (this.accumulator >= FIXED_STEP) {
      this.update(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
      if (this.state === "results") {
        this.accumulator = 0;
        break;
      }
    }

    this.draw(now / 1_000);
    this.animationFrame = requestAnimationFrame(this.loop);
  };

  private bindEvents(): void {
    this.elements.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.elements.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.elements.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.elements.canvas.addEventListener("pointercancel", this.handlePointerCancel);
    this.elements.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.elements.overlay.addEventListener("click", this.handleOverlayClick);
    this.elements.pauseButton.addEventListener("click", () => this.togglePause());
    this.elements.soundButton.addEventListener("click", () => this.toggleSound());
    this.elements.fullscreenButton.addEventListener("click", () => void this.toggleFullscreen());
    window.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.state === "playing") this.pause();
    });
  }

  private readonly handleOverlayClick = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-action]");
    const action = target?.dataset.action;
    if (!action) return;

    void this.audio.unlock();
    if (action === "start" || action === "restart") this.startRound();
    if (action === "resume") this.resume();
    if (action === "menu") this.openMenu();
    if (action === "howto") {
      this.menuExpanded = true;
      this.renderOverlay();
    }
    if (action === "simple-menu") {
      this.menuExpanded = false;
      this.renderOverlay();
    }
    if (action === "sound") this.toggleSound();
    if (action === "fullscreen") void this.toggleFullscreen();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    if (event.key === "Escape" || event.key.toLowerCase() === "p") this.togglePause();
    if (event.key.toLowerCase() === "m") this.toggleSound();
    if (event.key === "Enter" && (this.state === "menu" || this.state === "results")) {
      void this.audio.unlock();
      this.startRound();
    }
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (this.state !== "playing" || this.activePointerId !== null) return;
    event.preventDefault();
    void this.audio.unlock();
    const point = this.toWorldPoint(event);
    this.pointerX = point.x;
    this.pointerY = point.y;

    const refugee = this.refugees
      .filter((candidate) => canGrab(candidate))
      .map((candidate) => ({ candidate, distance: Math.hypot(candidate.x - point.x, candidate.y - point.y) }))
      .filter(({ distance }) => distance < GRAB_RADIUS)
      .sort((left, right) => left.distance - right.distance)[0]?.candidate;

    if (!refugee) return;
    this.grabbed = refugee;
    this.activePointerId = event.pointerId;
    refugee.phase = "held";
    refugee.touched = true;
    refugee.boatId = null;
    refugee.velocityX = 0;
    refugee.velocityY = 0;
    refugee.angularVelocity = 0;
    refugee.bounces = 0;
    this.pointerSamples.length = 0;
    this.recordPointer(point.x, point.y, event.timeStamp);
    this.elements.canvas.setPointerCapture(event.pointerId);
    this.audio.grab();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    const point = this.toWorldPoint(event);
    this.pointerX = point.x;
    this.pointerY = point.y;
    this.recordPointer(point.x, point.y, event.timeStamp);
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (!this.grabbed || event.pointerId !== this.activePointerId) return;
    const point = this.toWorldPoint(event);
    this.pointerX = point.x;
    this.pointerY = point.y;
    this.recordPointer(point.x, point.y, event.timeStamp);
    this.releaseGrabbed(true);
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId) this.releaseGrabbed(this.state === "playing");
  };

  private releaseGrabbed(launch: boolean): void {
    const refugee = this.grabbed;
    if (!refugee) return;

    if (launch && this.state === "playing") {
      refugee.phase = "flying";
      refugee.x = this.pointerX;
      refugee.y = this.pointerY;
      const velocity = calculateThrowVelocity(this.pointerSamples, MAX_THROW_SPEED);
      refugee.velocityX = velocity.x;
      refugee.velocityY = velocity.y;
      refugee.angularVelocity = clamp(velocity.x / 190, -7, 7);
      const speed = Math.hypot(velocity.x, velocity.y);
      this.audio.launch(speed);
      this.audio.scream(speed, refugee.id);
      if (speed > 900) this.shake = Math.max(this.shake, speed / 200);
    } else {
      refugee.phase = "flying";
      refugee.velocityX = 0;
      refugee.velocityY = 0;
    }

    const pointerId = this.activePointerId;
    if (pointerId !== null && this.elements.canvas.hasPointerCapture(pointerId)) {
      this.elements.canvas.releasePointerCapture(pointerId);
    }
    this.grabbed = null;
    this.activePointerId = null;
    this.pointerSamples.length = 0;
  }

  private recordPointer(x: number, y: number, time: number): void {
    this.pointerSamples.push({ x, y, time });
    const cutoff = time - 180;
    while ((this.pointerSamples[0]?.time ?? time) < cutoff) this.pointerSamples.shift();
  }

  private update(delta: number): void {
    if (this.state === "paused" || this.state === "results" || this.state === "menu") return;

    if (this.state === "countdown") {
      this.countdownRemaining -= delta;
      const beat = Math.ceil(this.countdownRemaining);
      if (beat !== this.countdownBeat && beat > 0) {
        this.countdownBeat = beat;
        this.audio.countdown(false);
      }
      if (this.countdownRemaining <= 0) {
        this.audio.countdown(true);
        this.setState("playing");
      }
      return;
    }

    this.elapsed += delta;
    this.updateSpawning(delta);
    for (const boat of this.boats) this.updateBoat(boat, delta);
    for (const refugee of this.refugees) this.updateRefugee(refugee, delta);
    this.updateEffects(delta);
    this.shake = Math.max(0, this.shake - delta * 18);
    this.flash = Math.max(0, this.flash - delta * 2.4);

    for (const boat of this.boats) {
      if (boat.sinking) continue;
      const crewActive = this.refugees.some(
        (refugee) => refugee.originBoatId === boat.id && isActiveCrewPhase(refugee.phase),
      );
      if (!crewActive) this.sinkBoat(boat);
    }
    for (let index = this.boats.length - 1; index >= 0; index -= 1) {
      const boat = this.boats[index];
      if (!boat) continue;
      if (boat.sinking) {
        if (boat.sinkTimer <= 0) this.boats.splice(index, 1);
        continue;
      }
      if (boat.x - boat.spec.length / 2 <= SHORE_X + 6) this.beachBoat(boat, index);
    }
    for (let index = this.refugees.length - 1; index >= 0; index -= 1) {
      if (this.refugees[index]?.phase === "gone") this.refugees.splice(index, 1);
    }

    this.updateHud();
  }

  private updateSpawning(delta: number): void {
    this.spawnTimer -= delta;
    if (this.spawnTimer > 0) return;
    this.spawnTimer = spawnIntervalSeconds(this.elapsed) * (0.72 + this.gameplayRandom.next() * 0.56);
    if (!canSpawnBoat(this.boats.length, this.refugees.length)) return;

    const spec = chooseBoatType(this.gameplayRandom, this.elapsed);
    const speed = boatSpeed(this.gameplayRandom, spec, this.elapsed);
    this.spawnBoat(spec, BOAT_SPAWN_X, speed, passengerCount(this.gameplayRandom, spec));
  }

  private spawnBoat(spec: BoatSpec, x: number, speed: number, passengers: number): Boat {
    const boat: Boat = {
      id: this.boatId += 1,
      spec,
      x,
      speed,
      wobble: this.gameplayRandom.next() * Math.PI * 2,
      sinking: false,
      sinkTimer: 0,
    };
    this.boats.push(boat);

    const usable = spec.length - 44;
    for (let seat = 0; seat < passengers; seat += 1) {
      const ratio = passengers === 1 ? 0.5 : seat / (passengers - 1);
      this.refugees.push(this.createRefugee(boat.id, -usable / 2 + ratio * usable));
    }
    return boat;
  }

  private createRefugee(boatId: number | null, seatOffset: number): Refugee {
    const skin = SKINS[this.refugeeId % SKINS.length] ?? SKINS[0];
    const shirt = SHIRTS[this.refugeeId % SHIRTS.length] ?? SHIRTS[0];
    const refugee: Refugee = {
      id: this.refugeeId += 1,
      boatId,
      originBoatId: boatId,
      seatOffset,
      x: 0,
      y: 0,
      previousX: 0,
      previousY: 0,
      velocityX: 0,
      velocityY: 0,
      phase: boatId === null ? "flying" : "seated",
      touched: false,
      rotation: 0,
      angularVelocity: 0,
      bounces: 0,
      runDelay: 0,
      runSpeed: 78 + this.gameplayRandom.next() * 42,
      swimTimer: 0,
      sinkTimer: 0,
      fade: 1,
      anim: this.gameplayRandom.next() * Math.PI * 2,
      skin: skin[0],
      skinShadow: skin[1],
      shirt: shirt[0],
      shirtShadow: shirt[1],
      vest: this.gameplayRandom.next() < 0.45,
      hair: HAIR[this.refugeeId % HAIR.length] ?? "fade",
      blinkTimer: 0.6 + this.visualRandom.next() * 4,
    };
    return refugee;
  }

  private updateBoat(boat: Boat, delta: number): void {
    if (boat.sinking) {
      boat.sinkTimer -= delta;
      boat.wobble += delta * 7;
      if (this.visualRandom.next() < delta * 16) {
        this.spawnBubbles(boat.x + (this.visualRandom.next() - 0.5) * boat.spec.length * 0.7, WATER_Y);
      }
      return;
    }
    boat.x -= boat.speed * delta;
    boat.wobble += delta * (1.6 + boat.speed / 90);
  }

  private updateRefugee(refugee: Refugee, delta: number): void {
    refugee.previousX = refugee.x;
    refugee.previousY = refugee.y;
    refugee.blinkTimer -= delta;
    if (refugee.blinkTimer < -0.12) refugee.blinkTimer = 1.6 + this.visualRandom.next() * 3.5;

    if (refugee.phase === "seated") {
      const boat = this.boats.find((candidate) => candidate.id === refugee.boatId);
      if (!boat) {
        refugee.phase = "gone";
        return;
      }
      refugee.x = boat.x + refugee.seatOffset;
      refugee.y = WATER_Y - 34 + Math.sin(boat.wobble * 2.2 + refugee.seatOffset * 0.1) * 2.5;
      return;
    }

    if (refugee.phase === "held") {
      const follow = 1 - Math.exp(-delta * 22);
      refugee.x += (this.pointerX - refugee.x) * follow;
      refugee.y += (this.pointerY - refugee.y) * follow;
      refugee.rotation +=
        (Math.atan2(this.pointerY - refugee.y, this.pointerX - refugee.x) * 0.1 - refugee.rotation) * follow;
      return;
    }

    if (refugee.phase === "flying") {
      refugee.velocityY += GRAVITY * delta;
      refugee.velocityX *= Math.pow(0.998, delta * 120);
      refugee.x += refugee.velocityX * delta;
      refugee.y += refugee.velocityY * delta;
      refugee.rotation += refugee.angularVelocity * delta;
      refugee.anim += delta * 14;

      if (refugee.x > WIDTH + 170) {
        this.scoreDeportation(refugee, WIDTH - 60, 3, "ORBITAL");
        return;
      }
      if (refugee.x < DUNE_X - 60) {
        refugee.phase = "gone";
        this.registerLanding(refugee.x < 0 ? 40 : refugee.x, "TOOK THE LAND ROUTE");
        return;
      }
      if (refugee.originBoatId !== null && refugee.velocityY > 0) {
        const ownBoat = this.boats.find(
          (candidate) => candidate.id === refugee.originBoatId && !candidate.sinking,
        );
        if (
          ownBoat &&
          landsOnBoatDeck(refugee.x, refugee.y, refugee.velocityY, ownBoat.x, ownBoat.spec.length)
        ) {
          this.reboardBoat(refugee, ownBoat);
          return;
        }
      }
      if (refugee.y >= LAND_Y && refugee.velocityY > 0) {
        if (refugee.x > SHORE_X + 2) this.splashDown(refugee);
        else this.sandDown(refugee);
      }
      return;
    }

    if (refugee.phase === "swimming") {
      refugee.swimTimer -= delta;
      refugee.anim += delta * 6;
      refugee.y = WATER_Y - 4 + Math.sin(refugee.anim) * 2;
      if (refugee.swimTimer <= 0) {
        refugee.phase = "sinking";
        refugee.sinkTimer = 0.85;
        this.spawnBubbles(refugee.x, WATER_Y);
      }
      return;
    }

    if (refugee.phase === "sinking") {
      refugee.sinkTimer -= delta;
      refugee.y += 30 * delta;
      refugee.rotation += delta * 0.8;
      refugee.fade = clamp(refugee.sinkTimer / 0.85, 0, 1);
      if (refugee.sinkTimer <= 0) refugee.phase = "gone";
      return;
    }

    if (refugee.phase === "shore") {
      if (refugee.runDelay > 0) {
        refugee.runDelay -= delta;
        return;
      }
      refugee.x -= refugee.runSpeed * delta;
      refugee.y = LAND_Y;
      refugee.anim += refugee.runSpeed * delta * 0.09;
      if (refugee.x <= DUNE_X) {
        refugee.phase = "gone";
        this.registerLanding(DUNE_X + 60, "MADE IT");
      }
    }
  }

  private splashDown(refugee: Refugee): void {
    const points = pointsForSplash(refugee.x);
    const impact = Math.min(1, Math.hypot(refugee.velocityX, refugee.velocityY) / 1_400);
    if (points > 0) {
      this.scoreDeportation(refugee, refugee.x, points);
      return;
    }
    refugee.phase = "swimming";
    refugee.y = WATER_Y - 4;
    refugee.velocityX = 0;
    refugee.velocityY = 0;
    refugee.rotation = 0;
    refugee.swimTimer = 1.7 + this.visualRandom.next() * 0.9;
    this.audio.splash(0.3 + impact * 0.3);
    this.spawnSplash(refugee.x, WATER_Y, 10, 0.5);
    this.floatingTexts.push({
      x: refugee.x,
      y: WATER_Y - 70,
      text: "TOO SHORT",
      color: "#b8c4cc",
      life: 0.9,
    });
  }

  private scoreDeportation(refugee: Refugee, splashX: number, points: number, prefix = ""): void {
    const multiplier = comboMultiplier(this.comboStreak);
    const gained = points * multiplier;
    this.score += gained;
    this.comboStreak += 1;
    this.deported += 1;
    this.best = Math.max(this.best, this.score);
    this.longestYeet = Math.max(this.longestYeet, Math.round(splashX - SHORE_X));
    refugee.phase = "sinking";
    refugee.sinkTimer = 0.85;
    refugee.x = splashX;
    refugee.y = WATER_Y - 4;
    const impact = Math.min(1, Math.hypot(refugee.velocityX, refugee.velocityY) / 1_400);
    refugee.velocityX = 0;
    refugee.velocityY = 0;
    this.audio.splash(0.5 + impact * 0.5);
    this.audio.deport(this.comboStreak);
    this.shake = Math.max(this.shake, 2 + impact * 5);
    this.spawnSplash(splashX, WATER_Y, 18 + Math.floor(impact * 16), 0.6 + impact * 0.7);
    const comboLabel = multiplier > 1 ? ` · COMBO ×${multiplier}` : "";
    this.floatingTexts.push({
      x: splashX,
      y: WATER_Y - 84,
      text: `${prefix ? `${prefix} · ` : ""}+${gained}${comboLabel}`,
      color: multiplier > 1 ? "#ffe274" : "#ffffff",
      life: 1.2,
    });
  }

  private sandDown(refugee: Refugee): void {
    const speed = Math.hypot(refugee.velocityX, refugee.velocityY);
    if (speed > 380 && refugee.bounces === 0) {
      refugee.bounces += 1;
      refugee.y = LAND_Y;
      refugee.velocityY *= -0.3;
      refugee.velocityX *= 0.55;
      refugee.angularVelocity *= 0.6;
      this.spawnDust(refugee.x, WATER_Y - 4, 5);
      this.audio.splash(0.25);
      return;
    }
    refugee.phase = "shore";
    refugee.y = LAND_Y;
    refugee.velocityX = 0;
    refugee.velocityY = 0;
    refugee.rotation = 0;
    refugee.angularVelocity = 0;
    refugee.runDelay = 0.35;
    this.spawnDust(refugee.x, WATER_Y - 4, 6);
    this.floatingTexts.push({
      x: refugee.x + 40,
      y: WATER_Y - 110,
      text: "ON THE BEACH!",
      color: "#ff9d6b",
      life: 1.1,
    });
  }

  private beachBoat(boat: Boat, index: number): void {
    this.boats.splice(index, 1);
    this.audio.boatBeach();
    this.shake = Math.max(this.shake, 4);
    this.floatingTexts.push({
      x: SHORE_X + 120,
      y: WATER_Y - 160,
      text: "BOAT BEACHED!",
      color: "#ff9d6b",
      life: 1.2,
    });

    let order = 0;
    for (const refugee of this.refugees) {
      if (refugee.boatId !== boat.id || refugee.phase !== "seated") continue;
      refugee.phase = "shore";
      refugee.boatId = null;
      refugee.x = Math.max(SHORE_X - 30, boat.x + refugee.seatOffset);
      refugee.y = LAND_Y;
      refugee.runDelay = 0.2 + order * 0.24;
      order += 1;
    }
  }

  private sinkBoat(boat: Boat): void {
    boat.sinking = true;
    boat.sinkTimer = BOAT_SINK_SECONDS;
    this.audio.splash(0.7);
    this.spawnBubbles(boat.x, WATER_Y);
    this.floatingTexts.push({
      x: boat.x,
      y: WATER_Y - 120,
      text: "SCUTTLED!",
      color: "#9fd8e8",
      life: 1.1,
    });
  }

  private reboardBoat(refugee: Refugee, boat: Boat): void {
    const usable = boat.spec.length - 44;
    refugee.phase = "seated";
    refugee.boatId = boat.id;
    refugee.seatOffset = clamp(refugee.x - boat.x, -usable / 2, usable / 2);
    refugee.velocityX = 0;
    refugee.velocityY = 0;
    refugee.rotation = 0;
    refugee.angularVelocity = 0;
    refugee.fade = 1;
    this.audio.grab();
    this.spawnSplash(refugee.x, WATER_Y, 6, 0.35);
    this.floatingTexts.push({
      x: boat.x,
      y: WATER_Y - 110,
      text: "BACK ABOARD!",
      color: "#8fe3a0",
      life: 1,
    });
  }

  private registerLanding(x: number, label: string): void {
    this.strikes = addStrikes(this.strikes, 1);
    this.slippedThrough += 1;
    this.comboStreak = 0;
    this.flash = 1;
    this.shake = Math.max(this.shake, 7);
    this.audio.strike();
    this.floatingTexts.push({ x, y: WATER_Y - 150, text: label, color: "#ff6b4a", life: 1.3 });
    if (isGameOver(this.strikes)) this.finishRound();
  }

  private updateEffects(delta: number): void {
    for (const particle of this.particles) {
      particle.life -= delta;
      particle.velocityY += particle.gravity * delta;
      particle.x += particle.velocityX * delta;
      particle.y += particle.velocityY * delta;
    }
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      if ((this.particles[index]?.life ?? 0) <= 0) this.particles.splice(index, 1);
    }
    if (this.particles.length > 420) this.particles.splice(0, this.particles.length - 420);
    for (const text of this.floatingTexts) {
      text.life -= delta;
      text.y -= 42 * delta;
    }
    for (let index = this.floatingTexts.length - 1; index >= 0; index -= 1) {
      if ((this.floatingTexts[index]?.life ?? 0) <= 0) this.floatingTexts.splice(index, 1);
    }
  }

  private startRound(): void {
    if (this.state !== "menu" && this.state !== "results") return;
    this.gameplayRandom = new SeededRandom(randomSeed());
    this.boats.length = 0;
    this.refugees.length = 0;
    this.particles.length = 0;
    this.floatingTexts.length = 0;
    this.score = 0;
    this.comboStreak = 0;
    this.strikes = 0;
    this.deported = 0;
    this.slippedThrough = 0;
    this.longestYeet = 0;
    this.elapsed = 0;
    this.spawnTimer = 1.6;
    this.bestBeforeRun = this.best;
    this.countdownRemaining = 3.15;
    this.countdownBeat = 4;
    this.accumulator = 0;
    this.flash = 0;
    this.shake = 0;
    this.setState("countdown");
  }

  private finishRound(): void {
    if (this.state === "results") return;
    this.releaseGrabbed(false);
    this.save.best = Math.max(this.save.best, this.score);
    this.save.totalDeported += this.deported;
    this.best = this.save.best;
    writeStoredValue(SAVE_KEY, JSON.stringify(this.save));
    this.audio.gameOver();
    this.setState("results");
  }

  private pause(): void {
    if (this.state !== "playing" && this.state !== "countdown") return;
    this.previousState = this.state;
    this.releaseGrabbed(false);
    this.setState("paused");
  }

  private resume(): void {
    if (this.state !== "paused") return;
    this.lastFrame = performance.now();
    this.accumulator = 0;
    this.setState(this.previousState === "countdown" ? "countdown" : "playing");
  }

  private togglePause(): void {
    if (this.state === "paused") this.resume();
    else this.pause();
  }

  private openMenu(): void {
    this.menuExpanded = false;
    this.boats.length = 0;
    this.refugees.length = 0;
    this.particles.length = 0;
    this.floatingTexts.length = 0;
    this.score = 0;
    this.comboStreak = 0;
    this.strikes = 0;
    this.setState("menu");
    this.updateHud();
  }

  private setState(state: GameState): void {
    this.state = state;
    this.elements.shell.dataset.state = state;
    this.renderOverlay();
    this.updateControls();
  }

  private renderOverlay(): void {
    if (this.state === "playing" || this.state === "countdown") {
      this.elements.overlay.hidden = true;
      return;
    }

    this.elements.overlay.hidden = false;
    if (this.state === "menu") {
      this.elements.overlay.innerHTML = this.renderMenu();
      return;
    }

    if (this.state === "paused") {
      this.elements.overlay.innerHTML = `
        <section class="dialog-card" aria-labelledby="pause-title">
          <div class="eyebrow">HOLD POSITION</div>
          <h2 id="pause-title">Patrol paused</h2>
          <p>The boats wait for nobody. Except now. Now they wait.</p>
          <button class="primary-button" data-action="resume">RESUME</button>
          <button class="secondary-button" data-action="menu">QUIT TO MENU</button>
        </section>
      `;
      return;
    }

    const newBest = this.score > this.bestBeforeRun;
    this.elements.overlay.innerHTML = `
      <section class="dialog-card results-card" aria-labelledby="results-title">
        <div class="eyebrow">COAST OVERRUN</div>
        <h2 id="results-title">${newBest ? "New personal best" : "Patrol over"}</h2>
        <div class="final-score"><strong>${this.score}</strong><span>POINTS</span></div>
        <div class="results-best">Deported <b>${this.deported}</b> · Slipped through <b>${this.slippedThrough}</b> · Longest yeet <b>${Math.round(this.longestYeet / 12)}m</b></div>
        <div class="results-best">Personal best <b>${this.best}</b> · Career deportations <b>${this.save.totalDeported}</b></div>
        <button class="primary-button" data-action="restart">PATROL AGAIN</button>
        <button class="secondary-button" data-action="menu">MAIN MENU</button>
      </section>
    `;
  }

  private renderMenu(): string {
    if (!this.menuExpanded) {
      return `
        <section class="menu-card quick-menu" aria-labelledby="game-title">
          <div class="eyebrow">SHITGATE INTERACTIVE PRESENTS</div>
          <h1 id="game-title"><span>RAPID</span> REFUGEES</h1>
          <p class="quick-instruction">DRAG THEM OUT OF THE BOATS · YEET THEM BACK TO SEA</p>
          <div class="quick-best"><span>BEST</span><strong>${this.save.best}</strong></div>
          <button class="primary-button quick-play" data-action="start">PLAY</button>
          <button class="secondary-button" data-action="howto">HOW TO PATROL</button>
        </section>
      `;
    }

    return `
      <section class="menu-card" aria-labelledby="game-title">
        <div class="eyebrow">BEACH BRIEFING</div>
        <h1 id="game-title"><span>RAPID</span> REFUGEES</h1>
        <ul class="briefing-list">
          <li><b>GRAB</b> passengers straight out of the boats. One grab each — no second chances.</li>
          <li><b>YEET</b> them past the buoy line. Longer throws score 1–3× and build your combo.</li>
          <li><b>SHORT SPLASHES</b> score nothing. They just bob there, thinking about what you did.</li>
          <li><b>HOLD THE BEACH.</b> Anyone who reaches the dunes costs a strike. Three strikes and the coast is overrun.</li>
          <li><b>EMPTY BOATS SINK</b> once the whole crew is in the drink. Yeet one back onto his own boat and the legend climbs right back in.</li>
        </ul>
        <button class="primary-button" data-action="start">PLAY</button>
        <div class="menu-actions">
          <button data-action="simple-menu">Back</button>
          <a href="/store">Store page</a>
        </div>
      </section>
    `;
  }

  private updateControls(): void {
    this.elements.pauseButton.disabled = this.state === "menu" || this.state === "results";
    this.elements.pauseButton.textContent = this.state === "paused" ? "▶" : "Ⅱ";
    this.elements.pauseButton.setAttribute("aria-label", this.state === "paused" ? "Resume" : "Pause");
    this.elements.soundButton.textContent = this.soundEnabled ? "◖))" : "◖×";
    this.elements.soundButton.setAttribute("aria-label", this.soundEnabled ? "Mute sound" : "Enable sound");
  }

  private toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    this.audio.setEnabled(this.soundEnabled);
    writeStoredValue("rapid-refugees-sound", this.soundEnabled ? "on" : "off");
    if (this.soundEnabled) void this.audio.unlock();
    this.updateControls();
    if (this.state === "menu") this.renderOverlay();
  }

  private async toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await this.elements.shell.requestFullscreen();
  }

  private updateHud(): void {
    this.elements.score.textContent = String(this.score).padStart(2, "0");
    this.elements.combo.textContent = this.comboStreak >= 2 ? `×${this.comboStreak}` : "—";
    this.elements.best.textContent = String(this.best).padStart(2, "0");
    this.elements.lives.textContent = "♥".repeat(Math.max(0, 3 - this.strikes)) + "♡".repeat(this.strikes);
    this.elements.lives.parentElement?.classList.toggle("urgent", this.strikes === 2 && this.state === "playing");
    const minutes = Math.floor(this.elapsed / 60);
    const seconds = Math.floor(this.elapsed % 60);
    this.elements.clock.textContent = `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  private draw(time: number): void {
    const context = this.context;
    context.save();
    if (this.shake > 0 && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      context.translate((this.visualRandom.next() - 0.5) * this.shake, (this.visualRandom.next() - 0.5) * this.shake);
    }
    this.drawScene(time);
    for (const refugee of this.refugees) {
      if (refugee.phase === "sinking") this.drawRefugee(refugee);
    }
    this.drawWaterSurface(time);
    for (const boat of this.boats) this.drawBoat(boat, time);
    for (const refugee of this.refugees) {
      if (refugee.phase === "swimming") this.drawSwimmer(refugee, time);
    }
    for (const refugee of this.refugees) {
      if (refugee.phase !== "sinking" && refugee.phase !== "swimming" && refugee.phase !== "seated") {
        this.drawRefugee(refugee);
      }
    }
    this.drawEffects();
    if (this.state === "countdown") this.drawCountdown();
    if (this.flash > 0) {
      context.globalAlpha = this.flash * 0.24;
      context.fillStyle = "#c22f1e";
      context.fillRect(0, 0, WIDTH, HEIGHT);
      context.globalAlpha = 1;
    }
    context.restore();
  }

  private drawScene(time: number): void {
    const context = this.context;
    const sky = context.createLinearGradient(0, 0, 0, WATER_Y);
    sky.addColorStop(0, "#1b3264");
    sky.addColorStop(0.55, "#487eae");
    sky.addColorStop(0.85, "#e0a271");
    sky.addColorStop(1, "#f0c19c");
    context.fillStyle = sky;
    context.fillRect(0, 0, WIDTH, WATER_Y);

    const sunGlow = context.createRadialGradient(1065, 175, 10, 1065, 175, 130);
    sunGlow.addColorStop(0, "rgba(255,236,190,.9)");
    sunGlow.addColorStop(1, "rgba(255,236,190,0)");
    context.fillStyle = sunGlow;
    context.beginPath();
    context.arc(1065, 175, 130, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#ffedc2";
    context.beginPath();
    context.arc(1065, 175, 42, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = "rgba(255,244,224,.35)";
    for (let index = 0; index < 4; index += 1) {
      const x = 160 + index * 310 + Math.sin(time * 0.08 + index * 2) * 18;
      const y = 90 + (index % 2) * 64;
      context.beginPath();
      context.ellipse(x, y, 68, 16, 0, 0, Math.PI * 2);
      context.ellipse(x + 42, y + 6, 46, 12, 0, 0, Math.PI * 2);
      context.fill();
    }

    const sea = context.createLinearGradient(0, WATER_Y - 20, 0, HEIGHT);
    sea.addColorStop(0, "#3d9db8");
    sea.addColorStop(0.35, "#2b7f9e");
    sea.addColorStop(1, "#124f6b");
    context.fillStyle = sea;
    context.fillRect(SHORE_X - 40, WATER_Y - 6, WIDTH - SHORE_X + 40, HEIGHT - WATER_Y + 6);

    const reflection = context.createLinearGradient(990, 0, 1140, 0);
    reflection.addColorStop(0, "rgba(255,236,190,0)");
    reflection.addColorStop(0.5, "rgba(255,236,190,.14)");
    reflection.addColorStop(1, "rgba(255,236,190,0)");
    context.fillStyle = reflection;
    context.fillRect(990, WATER_Y, 150, HEIGHT - WATER_Y);

    const sand = context.createLinearGradient(0, WATER_Y - 10, 0, HEIGHT);
    sand.addColorStop(0, "#ecd096");
    sand.addColorStop(1, "#cfa968");
    context.fillStyle = sand;
    context.beginPath();
    context.moveTo(0, WATER_Y - 4);
    context.lineTo(SHORE_X + 26, WATER_Y - 4);
    context.quadraticCurveTo(SHORE_X + 52, WATER_Y + HEIGHT * 0.28, SHORE_X + 84, HEIGHT);
    context.lineTo(0, HEIGHT);
    context.closePath();
    context.fill();

    const wetSand = context.createLinearGradient(SHORE_X - 20, 0, SHORE_X + 66, 0);
    wetSand.addColorStop(0, "rgba(236,208,150,0)");
    wetSand.addColorStop(0.55, "rgba(236,208,150,.55)");
    wetSand.addColorStop(1, "rgba(236,208,150,0)");
    context.fillStyle = wetSand;
    context.fillRect(SHORE_X - 20, WATER_Y - 4, 86, HEIGHT - WATER_Y + 4);

    context.fillStyle = "rgba(122,90,48,.16)";
    for (let index = 0; index < 46; index += 1) {
      const x = (index * 97) % (SHORE_X + 20);
      const y = WATER_Y + 12 + ((index * 53) % (HEIGHT - WATER_Y - 24));
      context.fillRect(x, y, 3, 2);
    }

    context.fillStyle = "#d9b073";
    context.beginPath();
    context.moveTo(0, HEIGHT);
    context.lineTo(0, WATER_Y + 26);
    context.quadraticCurveTo(52, WATER_Y - 24, 108, WATER_Y + 20);
    context.lineTo(108, HEIGHT);
    context.closePath();
    context.fill();
    context.strokeStyle = "rgba(90,110,60,.55)";
    context.lineWidth = 3;
    for (let blade = 0; blade < 9; blade += 1) {
      const baseX = 14 + blade * 11;
      context.beginPath();
      context.moveTo(baseX, WATER_Y + 8);
      context.quadraticCurveTo(baseX + 4, WATER_Y - 12, baseX + 9, WATER_Y - 20 - (blade % 3) * 5);
      context.stroke();
    }

    this.drawBeachProps();
    this.drawBuoyLine(time);
  }

  private drawBeachProps(): void {
    const context = this.context;
    context.strokeStyle = "#7a5a36";
    context.lineWidth = 11;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(176, WATER_Y + 6);
    context.quadraticCurveTo(168, WATER_Y - 58, 186, WATER_Y - 96);
    context.stroke();
    context.strokeStyle = "#4c7a3f";
    context.lineWidth = 7;
    for (let frond = 0; frond < 6; frond += 1) {
      const angle = -Math.PI * 0.9 + frond * 0.36;
      const tipX = 186 + Math.cos(angle) * 52;
      const tipY = WATER_Y - 96 + Math.sin(angle) * 30;
      context.beginPath();
      context.moveTo(186, WATER_Y - 96);
      context.quadraticCurveTo(186 + Math.cos(angle) * 28, WATER_Y - 104 + Math.sin(angle) * 16, tipX, tipY + 10);
      context.stroke();
    }

    context.strokeStyle = "#8a6a42";
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(318, WATER_Y + 4);
    context.lineTo(318, WATER_Y - 62);
    context.stroke();
    context.fillStyle = "#d8543e";
    context.beginPath();
    context.moveTo(318, WATER_Y - 96);
    context.lineTo(268, WATER_Y - 56);
    context.lineTo(368, WATER_Y - 56);
    context.closePath();
    context.fill();
    context.fillStyle = "#f2e3c2";
    context.beginPath();
    context.moveTo(318, WATER_Y - 96);
    context.lineTo(292, WATER_Y - 56);
    context.lineTo(318, WATER_Y - 56);
    context.closePath();
    context.fill();

    context.save();
    context.translate(410, WATER_Y + 48);
    context.rotate(-0.1);
    context.fillStyle = "#4a90b8";
    context.fillRect(-26, -10, 52, 20);
    context.fillStyle = "#f2e3c2";
    context.fillRect(-26, -10, 10, 20);
    context.restore();
  }

  private drawBuoyLine(time: number): void {
    const context = this.context;
    const buoyX = SHORE_X + SAFE_DISTANCE;
    context.strokeStyle = "rgba(255,255,255,.3)";
    context.lineWidth = 3;
    context.setLineDash([8, 12]);
    context.beginPath();
    context.moveTo(buoyX, WATER_Y - 190);
    context.lineTo(buoyX, WATER_Y - 4);
    context.stroke();
    context.setLineDash([]);

    for (let index = 0; index < 3; index += 1) {
      const bob = Math.sin(time * 1.8 + index * 1.4) * 3;
      const y = WATER_Y - 6 - index * 12 + bob;
      const scale = 1 - index * 0.18;
      context.fillStyle = "#e8622b";
      context.beginPath();
      context.arc(buoyX, y - 8 * scale, 9 * scale, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#f7f0df";
      context.fillRect(buoyX - 9 * scale, y - 10 * scale, 18 * scale, 4 * scale);
    }

    context.fillStyle = "rgba(255,255,255,.55)";
    context.font = "700 13px system-ui";
    context.textAlign = "center";
    context.fillText("DEPORTATION LINE", buoyX, WATER_Y - 200);
  }

  private drawWaterSurface(time: number): void {
    const context = this.context;
    for (let row = 0; row < 3; row += 1) {
      const y = WATER_Y - 2 + row * 16;
      context.strokeStyle = `rgba(220,245,250,${0.4 - row * 0.11})`;
      context.lineWidth = 3 - row * 0.6;
      context.beginPath();
      for (let x = SHORE_X - 30; x <= WIDTH; x += 14) {
        const wave = Math.sin(x * 0.035 + time * (1.6 + row * 0.4) + row * 2) * (4 - row);
        if (x === SHORE_X - 30) context.moveTo(x, y + wave);
        else context.lineTo(x, y + wave);
      }
      context.stroke();
    }

    context.fillStyle = "rgba(240,250,252,.6)";
    for (let index = 0; index < 7; index += 1) {
      const x = SHORE_X - 18 + index * 10;
      const swell = Math.sin(time * 2.2 + index * 0.9) * 5;
      context.beginPath();
      context.ellipse(x, WATER_Y + 2 + swell * 0.4, 9, 3.5, 0, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawBoat(boat: Boat, time: number): void {
    const context = this.context;
    const sinkProgress = boat.sinking ? 1 - Math.max(0, boat.sinkTimer) / BOAT_SINK_SECONDS : 0;
    const wobble = Math.sin(boat.wobble * 2.2) * (boat.sinking ? 0.14 : 0.045);

    if (!boat.sinking) {
      context.save();
      context.globalAlpha = 0.3;
      context.fillStyle = "#dff2f6";
      for (let trail = 0; trail < 3; trail += 1) {
        const length = 26 + trail * 16 + boat.speed * 0.12;
        context.beginPath();
        context.ellipse(
          boat.x + boat.spec.length / 2 + 14 + trail * 20,
          WATER_Y + 2 + Math.sin(time * 3 + trail) * 2,
          length / 2,
          3.5,
          0,
          0,
          Math.PI * 2,
        );
        context.fill();
      }
      context.restore();
    }

    context.save();
    if (boat.sinking) context.globalAlpha = 1 - sinkProgress * 0.6;
    context.translate(boat.x, WATER_Y - 6 + sinkProgress * 46);
    context.rotate(wobble + sinkProgress * 0.32);
    const half = boat.spec.length / 2;

    if (boat.spec.id === "dinghy") {
      context.fillStyle = "#43484f";
      context.beginPath();
      context.roundRect(-half, -26, boat.spec.length, 30, 15);
      context.fill();
      context.fillStyle = "#26292f";
      context.beginPath();
      context.roundRect(-half + 12, -22, boat.spec.length - 24, 18, 9);
      context.fill();
      context.strokeStyle = "#5c636c";
      context.lineWidth = 3;
      context.beginPath();
      context.roundRect(-half, -26, boat.spec.length, 30, 15);
      context.stroke();
    } else if (boat.spec.id === "speedboat") {
      context.fillStyle = "#e8ecf0";
      context.beginPath();
      context.moveTo(-half, -6);
      context.lineTo(-half + 16, -26);
      context.lineTo(half - 24, -26);
      context.lineTo(half, -8);
      context.lineTo(half - 8, 2);
      context.lineTo(-half + 6, 2);
      context.closePath();
      context.fill();
      context.fillStyle = "#2b6ea8";
      context.fillRect(-half + 12, -14, boat.spec.length - 36, 5);
      context.fillStyle = "#9fc4dd";
      context.beginPath();
      context.moveTo(-half + 30, -26);
      context.lineTo(-half + 44, -40);
      context.lineTo(-half + 66, -40);
      context.lineTo(-half + 60, -26);
      context.closePath();
      context.fill();
      context.fillStyle = "#43484f";
      context.fillRect(half - 18, -22, 12, 26);
    } else {
      context.fillStyle = "#8a6a42";
      context.beginPath();
      context.roundRect(-half, -18, boat.spec.length, 22, 6);
      context.fill();
      context.strokeStyle = "#6b4f2e";
      context.lineWidth = 2;
      for (let plank = 1; plank < 5; plank += 1) {
        context.beginPath();
        context.moveTo(-half + (boat.spec.length / 5) * plank, -18);
        context.lineTo(-half + (boat.spec.length / 5) * plank, 4);
        context.stroke();
      }
      context.fillStyle = "#a8412f";
      context.beginPath();
      context.arc(-half + 12, -8, 9, 0, Math.PI * 2);
      context.arc(half - 12, -8, 9, 0, Math.PI * 2);
      context.fill();
    }

    for (const refugee of this.refugees) {
      if (refugee.phase === "seated" && refugee.boatId === boat.id) this.drawSeated(refugee, time);
    }
    context.restore();
  }

  private drawSeated(refugee: Refugee, time: number): void {
    const context = this.context;
    const bob = Math.sin(time * 2.4 + refugee.id * 1.3) * 1.5;
    context.save();
    context.translate(refugee.seatOffset, -20 + bob);

    context.fillStyle = refugee.shirt;
    context.beginPath();
    context.roundRect(-11, -16, 22, 18, 7);
    context.fill();
    if (refugee.vest) {
      context.fillStyle = "#e8792b";
      context.beginPath();
      context.roundRect(-11, -16, 22, 12, 6);
      context.fill();
    }
    this.drawHead(refugee, -26, 0.82);
    context.restore();
  }

  private drawSwimmer(refugee: Refugee, time: number): void {
    const context = this.context;
    context.save();
    context.translate(refugee.x, WATER_Y - 2);
    context.strokeStyle = "rgba(230,248,252,.7)";
    context.lineWidth = 2.5;
    context.beginPath();
    context.ellipse(0, 2, 16 + Math.sin(time * 3 + refugee.id) * 3, 4, 0, 0, Math.PI * 2);
    context.stroke();

    context.strokeStyle = refugee.skin;
    context.lineWidth = 6;
    context.lineCap = "round";
    const paddle = Math.sin(refugee.anim * 2.2);
    context.beginPath();
    context.moveTo(-6, -6);
    context.quadraticCurveTo(-14, -12 - paddle * 5, -18, -4 - paddle * 7);
    context.stroke();

    context.fillStyle = refugee.skin;
    context.beginPath();
    context.arc(2, -12, 10, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#171113";
    context.beginPath();
    context.arc(2, -16, 9, Math.PI, Math.PI * 2);
    context.fill();
    context.restore();
  }

  private drawRefugee(refugee: Refugee): void {
    const context = this.context;
    const running = refugee.phase === "shore" && refugee.runDelay <= 0;
    const flying = refugee.phase === "flying";
    const held = refugee.phase === "held";
    const stride = running ? Math.sin(refugee.anim * 2.4) : held ? 0.15 : 0.5;

    context.save();
    context.globalAlpha = 0.26 * refugee.fade;
    context.fillStyle = "#0d2027";
    context.beginPath();
    const shadowScale = clamp(1 - Math.max(0, WATER_Y - refugee.y) / 620, 0.2, 1);
    context.ellipse(refugee.x, WATER_Y + 2, 24 * shadowScale, 7 * shadowScale, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();

    context.save();
    context.translate(refugee.x, refugee.y);
    if (!running) context.rotate(refugee.rotation);
    context.globalAlpha = refugee.fade;
    context.lineCap = "round";
    context.lineJoin = "round";

    const flail = flying ? Math.sin(refugee.anim) * 0.9 : 0;
    const armSwing = running ? Math.sin(refugee.anim * 2.4 + Math.PI) * 0.6 : held ? -1.3 : -0.5 - flail;

    context.strokeStyle = refugee.skinShadow;
    context.lineWidth = 8;
    context.beginPath();
    if (flying || held) {
      context.moveTo(-8, 6);
      context.lineTo(-12 - flail * 6, 24);
      context.lineTo(-9, 36);
      context.moveTo(8, 6);
      context.lineTo(12 + flail * 6, 24);
      context.lineTo(10, 36);
    } else {
      context.moveTo(-8, 6);
      context.lineTo(-9 - stride * 6, 26);
      context.lineTo(-13 + stride * 5, 36);
      context.moveTo(8, 6);
      context.lineTo(9 + stride * 6, 26);
      context.lineTo(13 - stride * 5, 36);
    }
    context.stroke();

    context.strokeStyle = refugee.skin;
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(-13, -14);
    context.lineTo(-21, 0 + armSwing * 6);
    context.lineTo(-16, 13 + armSwing * 9);
    context.moveTo(13, -14);
    context.lineTo(21, 0 - armSwing * 6);
    context.lineTo(18, 13 - armSwing * 9);
    context.stroke();

    const shirtGradient = context.createLinearGradient(-16, -20, 16, 10);
    shirtGradient.addColorStop(0, refugee.shirt);
    shirtGradient.addColorStop(1, refugee.shirtShadow);
    context.fillStyle = shirtGradient;
    context.beginPath();
    context.roundRect(-16, -21, 32, 34, [9, 9, 5, 5]);
    context.fill();

    if (refugee.vest) {
      context.fillStyle = "#e8792b";
      context.beginPath();
      context.roundRect(-16, -21, 32, 20, [9, 9, 3, 3]);
      context.fill();
      context.strokeStyle = "#b3531a";
      context.lineWidth = 2.5;
      context.beginPath();
      context.moveTo(0, -20);
      context.lineTo(0, -2);
      context.stroke();
    }

    context.fillStyle = refugee.shirtShadow;
    context.beginPath();
    context.roundRect(-15, 7, 30, 12, 4);
    context.fill();

    this.drawHead(refugee, -40, 1);
    context.restore();
  }

  private drawHead(refugee: Refugee, centerY: number, scale: number): void {
    const context = this.context;
    context.save();
    context.translate(0, centerY);
    context.scale(scale, scale);

    const headGradient = context.createRadialGradient(-4, -4, 2, 0, 0, 20);
    headGradient.addColorStop(0, refugee.skin);
    headGradient.addColorStop(1, refugee.skinShadow);
    context.fillStyle = headGradient;
    context.beginPath();
    context.ellipse(0, 0, 17, 19, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = refugee.skin;
    context.beginPath();
    context.ellipse(16, 1, 3.5, 6, 0, 0, Math.PI * 2);
    context.fill();

    this.drawHair(refugee);

    const blinking = refugee.blinkTimer < 0;
    context.strokeStyle = "#20150f";
    context.fillStyle = "#f7f0df";
    context.lineWidth = 1.8;
    if (blinking) {
      context.beginPath();
      context.moveTo(-8, -1);
      context.lineTo(-3, -1);
      context.moveTo(4, -1);
      context.lineTo(9, -1);
      context.stroke();
    } else {
      context.beginPath();
      context.ellipse(-5, -1, 3.2, 2.4, 0, 0, Math.PI * 2);
      context.ellipse(7, -1, 3.2, 2.4, 0, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#2a1a13";
      context.beginPath();
      context.arc(-4, -1, 1.3, 0, Math.PI * 2);
      context.arc(8, -1, 1.3, 0, Math.PI * 2);
      context.fill();
    }

    const screaming =
      refugee.phase === "flying" && Math.hypot(refugee.velocityX, refugee.velocityY) > 620;
    context.fillStyle = "#2b1712";
    context.strokeStyle = "rgba(53,24,14,.8)";
    context.lineWidth = 1.6;
    context.beginPath();
    if (screaming) {
      context.ellipse(2, 8, 3.6, 5.4, 0, 0, Math.PI * 2);
      context.fill();
    } else if (refugee.phase === "shore") {
      context.arc(2, 10, 4.4, Math.PI + 0.2, Math.PI * 2 - 0.2);
      context.stroke();
    } else {
      context.arc(2, 7, 4.4, 0.2, Math.PI - 0.2);
      context.stroke();
    }
    context.restore();
  }

  private drawHair(refugee: Refugee): void {
    const context = this.context;
    context.fillStyle = "#171113";
    if (refugee.hair === "fade" || refugee.hair === "buzz") {
      context.beginPath();
      context.ellipse(-1, -14, 16, refugee.hair === "buzz" ? 7 : 10, -0.08, Math.PI, Math.PI * 2);
      context.fill();
      return;
    }
    if (refugee.hair === "high-top") {
      context.beginPath();
      context.roundRect(-14, -28, 28, 18, [6, 6, 3, 3]);
      context.fill();
      return;
    }
    if (refugee.hair === "braids") {
      context.beginPath();
      context.ellipse(-1, -15, 16, 10, 0, Math.PI, Math.PI * 2);
      context.fill();
      context.strokeStyle = "#171113";
      context.lineWidth = 3.5;
      for (let index = -1; index <= 1; index += 1) {
        context.beginPath();
        context.moveTo(index * 6, -14);
        context.quadraticCurveTo(index * 9, -2, index * 10, 8);
        context.stroke();
      }
      return;
    }
    for (let index = 0; index < 10; index += 1) {
      const angle = (index / 9) * Math.PI;
      context.beginPath();
      context.arc(-14 + index * 3.1, -14 - Math.sin(angle) * 6, 5, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawEffects(): void {
    const context = this.context;
    for (const particle of this.particles) {
      context.globalAlpha = clamp(particle.life / particle.maximumLife, 0, 1);
      context.fillStyle = particle.color;
      context.beginPath();
      context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      context.fill();
    }
    context.textAlign = "center";
    context.font = "900 22px system-ui";
    for (const text of this.floatingTexts) {
      context.globalAlpha = clamp(text.life / 0.35, 0, 1);
      context.fillStyle = text.color;
      context.shadowColor = "rgba(0,20,30,.7)";
      context.shadowBlur = 8;
      context.fillText(text.text, text.x, text.y);
    }
    context.shadowBlur = 0;
    context.globalAlpha = 1;
  }

  private drawCountdown(): void {
    const context = this.context;
    const value = Math.max(1, Math.ceil(this.countdownRemaining));
    const pulse = 1 + (this.countdownRemaining % 1) * 0.22;
    context.fillStyle = "rgba(6,10,18,.3)";
    context.fillRect(0, 0, WIDTH, HEIGHT);
    context.save();
    context.translate(WIDTH / 2, HEIGHT / 2);
    context.scale(pulse, pulse);
    context.fillStyle = "#fff";
    context.shadowColor = "rgba(46,139,166,.9)";
    context.shadowBlur = 32;
    context.font = "900 112px system-ui";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(String(value), 0, 0);
    context.restore();
  }

  private spawnSplash(x: number, y: number, count: number, strength: number): void {
    const colors = ["#eaf8fb", "#b8e6f2", "#7fcbe0", "#ffffff"];
    for (let index = 0; index < count; index += 1) {
      const angle = -Math.PI * (0.15 + this.visualRandom.next() * 0.7);
      const speed = (120 + this.visualRandom.next() * 320) * strength;
      this.particles.push({
        x: x + (this.visualRandom.next() - 0.5) * 18,
        y,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed,
        life: 0.5 + this.visualRandom.next() * 0.45,
        maximumLife: 0.95,
        color: colors[index % colors.length] ?? "#ffffff",
        radius: 2 + this.visualRandom.next() * 4,
        gravity: 900,
      });
    }
  }

  private spawnBubbles(x: number, y: number): void {
    for (let index = 0; index < 8; index += 1) {
      this.particles.push({
        x: x + (this.visualRandom.next() - 0.5) * 22,
        y: y + this.visualRandom.next() * 10,
        velocityX: (this.visualRandom.next() - 0.5) * 24,
        velocityY: -30 - this.visualRandom.next() * 40,
        life: 0.5 + this.visualRandom.next() * 0.4,
        maximumLife: 0.9,
        color: "#cfeef7",
        radius: 1.5 + this.visualRandom.next() * 2.5,
        gravity: -60,
      });
    }
  }

  private spawnDust(x: number, y: number, count: number): void {
    for (let index = 0; index < count; index += 1) {
      this.particles.push({
        x: x + (this.visualRandom.next() - 0.5) * 24,
        y,
        velocityX: (this.visualRandom.next() - 0.5) * 70,
        velocityY: -20 - this.visualRandom.next() * 40,
        life: 0.25 + this.visualRandom.next() * 0.2,
        maximumLife: 0.45,
        color: "#dcbd84",
        radius: 3 + this.visualRandom.next() * 4,
        gravity: 540,
      });
    }
  }

  private prepareShot(kind: string): void {
    this.gameplayRandom = new SeededRandom(0xc0a57);
    this.setState("playing");
    this.elements.overlay.hidden = true;

    if (kind === "fleet") {
      this.score = 64;
      this.comboStreak = 2;
      this.elapsed = 52;
      this.spawnBoat(BOAT_SPECS.dinghy, 840, 0, 4);
      this.spawnBoat(BOAT_SPECS.speedboat, 1_020, 0, 2);
      this.spawnBoat(BOAT_SPECS.raft, 1_120, 0, 7);
      const swimmer = this.createRefugee(null, 0);
      swimmer.phase = "swimming";
      swimmer.touched = true;
      swimmer.x = 610;
      swimmer.y = WATER_Y - 4;
      swimmer.swimTimer = 99;
      this.refugees.push(swimmer);
      const runner = this.createRefugee(null, 0);
      runner.phase = "shore";
      runner.touched = true;
      runner.x = 238;
      runner.y = LAND_Y;
      this.refugees.push(runner);
    } else {
      this.score = 137;
      this.comboStreak = 5;
      this.strikes = 1;
      this.elapsed = 96;
      this.spawnBoat(BOAT_SPECS.dinghy, 1_060, 0, 4);
      this.spawnBoat(BOAT_SPECS.raft, 1_150, 0, 6);
      const flyer = this.createRefugee(null, 0);
      flyer.phase = "flying";
      flyer.touched = true;
      flyer.x = 740;
      flyer.y = 310;
      flyer.velocityX = 540;
      flyer.velocityY = 120;
      flyer.rotation = 0.55;
      this.refugees.push(flyer);
      this.spawnSplash(890, WATER_Y, 22, 1);
      this.floatingTexts.push({ x: 890, y: WATER_Y - 84, text: "+9 · COMBO ×2", color: "#ffe274", life: 1.2 });
    }

    for (const boat of this.boats) {
      for (const refugee of this.refugees) {
        if (refugee.phase === "seated" && refugee.boatId === boat.id) {
          refugee.x = boat.x + refugee.seatOffset;
          refugee.y = WATER_Y - 34;
        }
      }
    }
    this.updateHud();
    this.frozen = true;
  }

  private toWorldPoint(event: PointerEvent): { x: number; y: number } {
    const bounds = this.elements.canvas.getBoundingClientRect();
    return {
      x: clamp((event.clientX - bounds.left) * (WIDTH / bounds.width), 0, WIDTH),
      y: clamp((event.clientY - bounds.top) * (HEIGHT / bounds.height), 0, HEIGHT),
    };
  }
}
